/**
 * Collections page — list + create + delete + start ingestion.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Eye, Plus, Trash2, Play, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { ConfirmDangerButton } from "@/components/common/ConfirmDangerButton";
import {
  DetailFact,
  DetailGrid,
  DetailSection,
  MetadataTable,
  Metric,
  MetricStrip,
  formatDateTime,
  formatUnknown,
  truncateMiddle,
} from "@/components/common/AdminDetails";
import { ActionState, LoadingRow, EmptyState, ErrorState } from "@/components/common/StateViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { analyticsApi, collectionApi, tenantApi, type Collection, type CollectionDocumentCount, type Tenant } from "@/lib/proxy-api";
import { useHasInstance } from "@/lib/use-instance";
import { ApiException } from "@/lib/api-client";
import { canManageTenant, canWriteTenant, useTenantWorkspace } from "@/lib/tenant-workspace";

interface CollectionStats {
  total: number;
  active: number;
  archived: number;
  statuses: Record<string, number>;
  truncated: boolean;
}

export function CollectionsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const workspace = useTenantWorkspace();
  const [showCreate, setShowCreate] = useState(false);
  const [viewCollectionId, setViewCollectionId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const hasInstance = useHasInstance();
  const { data, isLoading, error } = useQuery({
    queryKey: ["collections", workspace.tenantId],
    queryFn: () => collectionApi.list({ tenantId: workspace.tenantId ?? undefined }),
    enabled: hasInstance && workspace.hasTenant,
  });
  const collections: Collection[] = data?.collections ?? [];
  const collectionIds = collections.map((collection) => collection.id);

  const tenantQ = useQuery({
    queryKey: ["tenant", workspace.tenantId],
    queryFn: () => tenantApi.get(workspace.tenantId!),
    enabled: hasInstance && workspace.hasTenant,
  });

  const analyticsQ = useQuery({
    queryKey: ["document-analytics", workspace.tenantId],
    queryFn: () => analyticsApi.documents({ tenantId: workspace.tenantId ?? undefined }),
    enabled: hasInstance && workspace.hasTenant && !isTenantKeyRequired(error),
  });

  const documentCountsQ = useQuery({
    queryKey: ["collection-document-counts", workspace.tenantId, collectionIds.join("|")],
    queryFn: () => collectionApi.documentCounts({
      tenantId: workspace.tenantId ?? undefined,
      collectionIds,
    }),
    enabled: hasInstance && workspace.hasTenant && collections.length > 0 && !isTenantKeyRequired(error),
    staleTime: 60_000,
  });

  const collectionDetailsQ = useQuery({
    queryKey: ["collection", workspace.tenantId, viewCollectionId],
    queryFn: () => collectionApi.get(viewCollectionId!, { tenantId: workspace.tenantId ?? undefined }),
    enabled: Boolean(viewCollectionId) && workspace.hasTenant,
  });

  const createMut = useMutation({
    mutationFn: () => collectionApi.create({ name, is_active: true }, { tenantId: workspace.tenantId ?? undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collections", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["collection-document-counts", workspace.tenantId] });
      setShowCreate(false);
      setName("");
      setFormError(null);
    },
    onError: (err) => setFormError(err instanceof ApiException ? err.message : "Failed to create collection"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => collectionApi.delete(id, true, { tenantId: workspace.tenantId ?? undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collections", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["collection-document-counts", workspace.tenantId] });
    },
  });

  const ingestMut = useMutation({
    mutationFn: (id: string) => collectionApi.startIngestion(id, { tenantId: workspace.tenantId ?? undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collections", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["collection-document-counts", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["document-analytics", workspace.tenantId] });
    },
  });

  const recreateMut = useMutation({
    mutationFn: (id: string) => collectionApi.recreateDatasource(id, { tenantId: workspace.tenantId ?? undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collections", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["collection-document-counts", workspace.tenantId] });
      qc.invalidateQueries({ queryKey: ["document-analytics", workspace.tenantId] });
    },
  });

  const statsByCollection = buildCollectionStats(collections, documentCountsQ.data?.counts ?? {});
  const activeCollections = collections.filter((collection) => collection.is_active).length;
  const exactDocumentTotal = collections.reduce((sum, collection) => sum + (statsByCollection.get(collection.id)?.total ?? 0), 0);
  const exactFailedDocuments = collections.reduce((sum, collection) => sum + (statsByCollection.get(collection.id)?.statuses.failed ?? 0), 0);
  const totalDocuments = documentCountsQ.data ? exactDocumentTotal : analyticsQ.data?.total_documents ?? 0;
  const failedDocuments = documentCountsQ.data ? exactFailedDocuments : analyticsQ.data?.by_status?.failed ?? 0;
  const countUnavailable = Boolean(documentCountsQ.error);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Collections"
        description="Logical groupings of documents. Each maps to one Bedrock Data Source."
        actions={
          canManageTenant(workspace.role) && workspace.hasTenant ? (
            <Button onClick={() => setShowCreate((v) => !v)}>
              <Plus className="h-4 w-4" /> New collection
            </Button>
          ) : undefined
        }
      />

      {!workspace.hasTenant ? (
        <EmptyState message="Select a tenant to view collections." />
      ) : showCreate && (
        <form
          className="rounded-lg border bg-card p-4 space-y-3"
          onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="c-name">Name</Label>
            <Input id="c-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button type="submit" disabled={createMut.isPending || !name}>Create collection</Button>
          </div>
        </form>
      )}

      {workspace.hasTenant && (isLoading ? (
        <LoadingRow cols={3} />
      ) : error ? (
        isTenantKeyRequired(error) && workspace.basePath ? (
          <ActionState
            message="This tenant needs a stored Tenant Admin API key before collections can be viewed."
            actionLabel="Open tenant overview"
            onAction={() => navigate(workspace.basePath!)}
          />
        ) : (
          <ErrorState message={error instanceof ApiException ? error.message : "Failed to load collections"} />
        )
      ) : collections.length === 0 ? (
        <EmptyState message="No collections yet." />
      ) : (
        <div className="space-y-4">
          <MetricStrip>
            <Metric label="Collections" value={collections.length} note={`${activeCollections} active`} />
            <Metric
              label="Documents"
              value={documentCountsQ.isLoading ? "Loading" : countUnavailable ? "Unavailable" : totalDocuments}
              note={countUnavailable ? "Document counts unavailable" : documentCountsQ.data ? "Collection totals" : "Analytics estimate"}
            />
            <Metric
              label="Failed documents"
              value={documentCountsQ.isLoading ? "Loading" : countUnavailable ? "Unavailable" : failedDocuments}
              note={failedDocuments > 0 ? "Needs investigation" : documentCountsQ.data ? "Collection totals" : "Analytics estimate"}
            />
            <Metric
              label="Effective LLM"
              value={tenantQ.data?.effective_defaults?.llm_model ?? tenantQ.data?.llm_provider?.default_model ?? "-"}
              note={tenantQ.data?.effective_defaults?.llm_provider_type ?? tenantQ.data?.llm_provider?.type ?? "Tenant default"}
            />
          </MetricStrip>

          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead>Data source</TableHead>
                  <TableHead>Chunking</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-16 text-right">Delete</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {collections.map((c) => {
                  const stats = statsByCollection.get(c.id) ?? emptyCollectionStats();
                  return (
                    <TableRow key={c.id}>
                      <TableCell>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{truncateMiddle(c.id, 12)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" title="View collection details" onClick={() => setViewCollectionId(c.id)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canWriteTenant(workspace.role) && <Button variant="ghost" size="icon" title="Start ingestion" disabled={ingestMut.isPending} onClick={() => ingestMut.mutate(c.id)}>
                            <Play className="h-4 w-4" />
                          </Button>}
                          {canManageTenant(workspace.role) && <Button variant="ghost" size="icon" title="Recreate data source (re-ingest)" disabled={recreateMut.isPending} onClick={() => recreateMut.mutate(c.id)}>
                            <RefreshCw className="h-4 w-4" />
                          </Button>}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{c.collection_type ?? "-"}</TableCell>
                      <TableCell>
                        <div className="font-medium">{documentCountsQ.isLoading ? "Loading" : countUnavailable ? "Unavailable" : formatCount(stats)}</div>
                        <div className="text-xs text-muted-foreground">{documentCountsQ.error ? "Count failed" : statusSummary(stats.statuses, stats.truncated)}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{collectionDataSourceLabel(c, tenantQ.data)}</TableCell>
                      <TableCell className="text-muted-foreground">{chunkingSummary(c.chunking_config)}</TableCell>
                      <TableCell>
                        <CollectionStatusBadge active={c.is_active} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{formatDateTime(c.updated_at ?? c.created_at)}</TableCell>
                      <TableCell className="text-right">
                        {canManageTenant(workspace.role) && (
                          <ConfirmDangerButton
                            title="Delete collection"
                            resourceName={c.name}
                            actionLabel="Delete collection"
                            disabled={deleteMut.isPending}
                            onConfirm={() => deleteMut.mutate(c.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </ConfirmDangerButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}

      <CollectionDetailsModal
        open={Boolean(viewCollectionId)}
        collection={collectionDetailsQ.data}
        isLoading={collectionDetailsQ.isLoading}
        error={collectionDetailsQ.error}
        onClose={() => setViewCollectionId(null)}
        onOpenTenantOverview={workspace.basePath ? () => navigate(workspace.basePath!) : undefined}
        tenant={tenantQ.data}
        stats={viewCollectionId ? statsByCollection.get(viewCollectionId) : undefined}
      />
    </div>
  );
}

function CollectionDetailsModal({
  open,
  collection,
  isLoading,
  error,
  onClose,
  onOpenTenantOverview,
  tenant,
  stats,
}: {
  open: boolean;
  collection?: Collection;
  isLoading: boolean;
  error: unknown;
  onClose: () => void;
  onOpenTenantOverview?: () => void;
  tenant?: Tenant;
  stats?: CollectionStats;
}) {
  if (!open) return null;

  const collectionStats = stats ?? emptyCollectionStats();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
      <div className="flex max-h-[88vh] w-full max-w-4xl flex-col rounded-lg border bg-card shadow-lg">
        <div className="flex items-center justify-between gap-3 border-b p-4">
          <div>
            <h2 className="font-semibold">{collection?.name ?? "Collection details"}</h2>
            {collection && <p className="text-sm text-muted-foreground">{collection.id}</p>}
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>

        <div className="overflow-auto p-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading collection…</p>
          ) : error ? (
            isTenantKeyRequired(error) && onOpenTenantOverview ? (
              <ActionState
                message="This tenant needs a stored Tenant Admin API key before collection details can be viewed."
                actionLabel="Open tenant overview"
                onAction={onOpenTenantOverview}
              />
            ) : (
              <ErrorState message={error instanceof ApiException ? error.message : "Failed to load collection"} />
            )
          ) : collection ? (
            <div className="space-y-4">
              <DetailSection title="Collection overview">
                <DetailGrid>
                  <DetailFact label="Type" value={collection.collection_type} />
                  <DetailFact label="Status" value={<CollectionStatusBadge active={collection.is_active} />} />
                  <DetailFact label="Documents" value={collectionStats.total} />
                  <DetailFact label="Active Documents" value={collectionStats.active} />
                  <DetailFact label="Archived Documents" value={collectionStats.archived} />
                  <DetailFact label="Parent Collection" value={collection.parent_collection_id} />
                  <DetailFact label="Data Source" value={collectionDataSourceLabel(collection, tenant)} />
                  <DetailFact label="Created" value={formatDateTime(collection.created_at)} />
                  <DetailFact label="Updated" value={formatDateTime(collection.updated_at)} />
                </DetailGrid>
                <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                  Counts are computed from the documents endpoint on the Admin UI server and are exact up to 10,000 documents per collection. Larger collections need a native ServisSearch count endpoint.
                </div>
              </DetailSection>

              {collection.description && (
                <div className="rounded-md border p-3 text-sm">
                  {collection.description}
                </div>
              )}

              <DetailSection title="Document health">
                <DetailGrid>
                  <DetailFact label="Completed" value={collectionStats.statuses.completed ?? 0} />
                  <DetailFact label="Processing" value={collectionStats.statuses.processing ?? 0} />
                  <DetailFact label="Pending" value={collectionStats.statuses.pending ?? 0} />
                  <DetailFact label="Failed" value={collectionStats.statuses.failed ?? 0} />
                </DetailGrid>
              </DetailSection>

              <DetailSection title="Chunking settings">
                <DetailGrid>
                  <DetailFact label="Strategy" value={formatChunkValue(collection.chunking_config, "strategy")} />
                  <DetailFact label="Max Tokens" value={formatChunkValue(collection.chunking_config, "max_tokens", "maxTokens")} />
                  <DetailFact label="Overlap" value={formatChunkValue(collection.chunking_config, "overlap_percent", "overlapPercent")} />
                  <DetailFact label="Buffer Size" value={formatChunkValue(collection.chunking_config, "buffer_size", "bufferSize")} />
                  <DetailFact label="Breakpoint Threshold" value={formatChunkValue(collection.chunking_config, "breakpoint_threshold", "breakpointPercentileThreshold")} />
                </DetailGrid>
              </DetailSection>

              <DetailSection title="Tenant knowledge base context">
                <DetailGrid>
                  <DetailFact label="Knowledge Base" value={tenant?.knowledge_base_id} />
                  <DetailFact label="Tenant Data Source" value={tenant?.data_source_id} />
                  <DetailFact label="Documents Bucket" value={tenant?.documents_bucket} />
                  <DetailFact label="Vector Index" value={tenant?.vector_index_name} />
                  <DetailFact label="Embedding Model" value={tenant?.effective_defaults?.embedding_model ?? truncateMiddle(tenant?.embedding_model_arn, 24)} className="lg:col-span-2" />
                </DetailGrid>
              </DetailSection>

              <DetailSection title="LLM defaults used by this collection">
                <DetailGrid>
                  <DetailFact label="Provider" value={tenant?.effective_defaults?.llm_provider_type ?? tenant?.llm_provider?.type} />
                  <DetailFact label="Model" value={tenant?.effective_defaults?.llm_model ?? tenant?.llm_provider?.default_model} />
                  <DetailFact label="Temperature" value={tenant?.effective_defaults?.llm_temperature} />
                  <DetailFact label="Max Tokens" value={tenant?.effective_defaults?.llm_max_tokens} />
                  <DetailFact label="Base URL" value={tenant?.llm_provider?.openai_config?.base_url} />
                  <DetailFact label="Provider Key" value={tenant?.llm_provider?.openai_config?.api_key ? "stored (masked)" : "not exposed"} />
                </DetailGrid>
              </DetailSection>

              <DetailSection title="Collection metadata">
                <MetadataTable value={collection.metadata} emptyLabel="No collection metadata exposed." />
              </DetailSection>

              <DetailSection title="Recent documents in this collection">
                <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                  Recent document preview is unavailable without loading document rows. Use the Documents page for the first 200 documents in this collection.
                </div>
              </DetailSection>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function isTenantKeyRequired(error: unknown): boolean {
  return error instanceof ApiException && error.code === "TENANT_KEY_REQUIRED";
}

function buildCollectionStats(collections: Collection[], counts: Record<string, CollectionDocumentCount>): Map<string, CollectionStats> {
  return new Map(collections.map((collection) => [
    collection.id,
    {
      total: counts[collection.id]?.total ?? 0,
      active: counts[collection.id]?.active ?? 0,
      archived: counts[collection.id]?.archived ?? 0,
      statuses: counts[collection.id]?.statuses ?? {},
      truncated: counts[collection.id]?.truncated ?? false,
    },
  ]));
}

function emptyCollectionStats(): CollectionStats {
  return { total: 0, active: 0, archived: 0, statuses: {}, truncated: false };
}

function statusSummary(statuses: Record<string, number>, truncated = false): string {
  const ordered = ["completed", "processing", "pending", "failed"]
    .filter((status) => statuses[status])
    .map((status) => `${statuses[status]} ${status}`);
  const summary = ordered.length > 0 ? ordered.join(" / ") : "No status breakdown";
  return truncated ? `${summary} / 10,000+` : summary;
}

function formatCount(stats: CollectionStats): string | number {
  return stats.truncated ? `${stats.total}+` : stats.total;
}

function collectionDataSourceLabel(collection: Collection, tenant?: Tenant): string {
  if (collection.data_source_id) return truncateMiddle(collection.data_source_id, 12);
  if (collection.has_data_source) return "Dedicated data source";
  if (tenant?.data_source_id) return `Tenant default (${truncateMiddle(tenant.data_source_id, 10)})`;
  return "Not exposed";
}

function chunkingSummary(config?: Record<string, unknown> | null): string {
  if (!config) return "Tenant default";
  const strategy = formatChunkValue(config, "strategy");
  const maxTokens = formatChunkValue(config, "max_tokens", "maxTokens");
  const overlap = formatChunkValue(config, "overlap_percent", "overlapPercent");
  return `${strategy}; ${maxTokens} tokens; ${overlap} overlap`;
}

function formatChunkValue(config: Record<string, unknown> | null | undefined, ...keys: string[]): string {
  if (!config) return "-";
  for (const key of keys) {
    const value = config[key];
    if (value !== undefined && value !== null && value !== "") return formatUnknown(value);
  }
  return "-";
}

function CollectionStatusBadge({ active }: { active: boolean }) {
  return (
    <Badge variant="outline" className={active ? "bg-green-50 text-green-700 border-green-200" : "bg-muted text-muted-foreground"}>
      {active ? "active" : "inactive"}
    </Badge>
  );
}
